import { getChain } from '~/constants/chains'
import { getNumberFromBigNumber } from '~/utils/numbers'

const MS_PER_DAY = 24 * 60 * 60 * 1000

export type OrgLot = {
  id: string
  chainId: number
  tokenAddress: string
  // Base units as integer strings, safe for the BigInt conversions below.
  quantity: string
  quantityRemaining: string
  unitPrice: string
  basisSource: string
  warnings: string[]
  acquisitionTime: string
  // The assertion anchor: how an edit or revoke addresses this lot across
  // rebuilds. Null source transfer = synthetic lot, not addressable.
  sourceTransferId: string | null
  originLotId: string | null
  smartAccountId: string
  smartAccountName: string
  smartAccountAddress: string
  tokenSymbol: string | null
  tokenDecimals: number | null
  sourceAddress: string | null
  sourceTransactionHash: string | null
  // The live edit assertion at this lot's anchor, sparse: null fields stay
  // derived. Null when nothing is asserted.
  asserted: {
    unitPrice: string | null
    acquisitionTime: string | null
    originLotId: string | null
  } | null
  // The latest assertion at this lot's anchor is a revoke. Optional so the
  // view tolerates an API that doesn't send it yet.
  revoked?: boolean
}

export type LotRow = {
  id: string
  lotNumber: number
  chainId: number
  tokenAddress: string
  decimals: number
  // Raw on-chain symbol, not the pinned display asset: the basis figures
  // track the vault share's own unit price.
  symbol: string
  smartAccountId: string
  smartAccountName: string
  smartAccountAddress: string
  sourceTransferId: string | null
  originLotId: string | null
  acquisitionTime: string
  heldDays: number
  sourceAddress: string | null
  sourceTransactionHash: string | null
  quantity: number
  quantityRemaining: number
  unitPrice: number
  costBasis: number
  basisSource: string
  warnings: string[]
  // Which fields carry a live assertion; a re-edit must resubmit them, since
  // the latest assertion row replaces the prior one wholesale.
  assertedFields: { unitPrice: boolean; acquisitionTime: boolean }
  // The live assertion differs from the projection: the row shows the values
  // the next recompute will land.
  pendingEdit: boolean
  // A live revoke hasn't landed yet: the projection still carries the
  // withdrawn edit until the next recompute.
  pendingRevert: boolean
}

// Lot numbers continue across pages so a lot keeps its position when paging.
export const buildLotRows = ({
  items,
  pageIndex,
  pageSize,
  openAsOf,
}: {
  items: OrgLot[]
  pageIndex: number
  pageSize: number
  openAsOf: Date
}): LotRow[] =>
  items.map((lot, index): LotRow => {
    // Natives have no erc20_tokens row; their unit lives on the chain config.
    const native = getChain(lot.chainId)?.nativeCurrency
    const decimals = lot.tokenDecimals ?? native?.decimals ?? 18
    const quantity = getNumberFromBigNumber(BigInt(lot.quantity), decimals)

    // The row shows the values the books will actually carry: a live
    // assertion overrides the projection until the recompute lands it.
    const assertedPrice =
      lot.asserted?.unitPrice != null ? Number(lot.asserted.unitPrice) : null
    const assertedTime = lot.asserted?.acquisitionTime ?? null
    const unitPrice = assertedPrice ?? Number(lot.unitPrice)
    const acquisitionTime = assertedTime ?? lot.acquisitionTime
    const pendingEdit =
      (assertedPrice !== null && assertedPrice !== Number(lot.unitPrice)) ||
      (assertedTime !== null &&
        new Date(assertedTime).getTime() !==
          new Date(lot.acquisitionTime).getTime())
    // An asserted basis with a live revoke means the projection still carries
    // the withdrawn edit.
    const pendingRevert = lot.revoked === true && lot.basisSource === 'asserted'

    return {
      id: lot.id,
      lotNumber: pageIndex * pageSize + index + 1,
      chainId: lot.chainId,
      tokenAddress: lot.tokenAddress,
      decimals,
      symbol: lot.tokenSymbol ?? native?.symbol ?? '',
      smartAccountId: lot.smartAccountId,
      smartAccountName: lot.smartAccountName,
      smartAccountAddress: lot.smartAccountAddress,
      sourceTransferId: lot.sourceTransferId,
      originLotId: lot.originLotId,
      acquisitionTime,
      heldDays: Math.max(
        0,
        Math.floor(
          (openAsOf.getTime() - new Date(acquisitionTime).getTime()) /
            MS_PER_DAY,
        ),
      ),
      sourceAddress: lot.sourceAddress,
      sourceTransactionHash: lot.sourceTransactionHash,
      quantity,
      quantityRemaining: getNumberFromBigNumber(
        BigInt(lot.quantityRemaining),
        decimals,
      ),
      unitPrice,
      costBasis: unitPrice * quantity,
      basisSource: lot.basisSource,
      warnings: lot.warnings,
      assertedFields: {
        unitPrice: assertedPrice !== null,
        acquisitionTime: assertedTime !== null,
      },
      pendingEdit,
      pendingRevert,
    }
  })
